import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, Button, Input, Field, Spinner, EmptyState, ErrorBanner, formatCents } from '../components/ui';

interface Service {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  priceCents: number;
}

interface Slot {
  startsAt: string;
  endsAt: string;
}

export function PublicBookingPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [slot, setSlot] = useState<Slot | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const servicesQ = useQuery({
    queryKey: ['public-services', tenantSlug],
    queryFn: () => api.get<{ data: Service[] }>(`/public/${tenantSlug}/services`),
  });

  const availQ = useQuery({
    queryKey: ['public-availability', tenantSlug, serviceId, date],
    queryFn: () =>
      api.get<{ data: Slot[] }>(`/public/${tenantSlug}/availability`, {
        serviceId: serviceId!,
        date,
      }),
    enabled: !!serviceId,
  });

  const bookMut = useMutation({
    mutationFn: () =>
      api.post(`/public/${tenantSlug}/bookings`, {
        serviceId,
        startsAt: slot!.startsAt,
        contactName: name,
        contactEmail: email,
        contactPhone: phone || undefined,
        notes: notes || undefined,
      }),
    onSuccess: () => setConfirmed(true),
    onError: (e) => setError(e instanceof Error ? e.message : 'Booking failed'),
  });

  if (servicesQ.isLoading) return <Spinner />;
  if (!servicesQ.data) {
    return (
      <Card style={{ maxWidth: 480, margin: '60px auto' }}>
        <EmptyState title="Booking page not available" description="Check the link with the business." />
      </Card>
    );
  }

  if (confirmed) {
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', padding: 'var(--space-4)' }}>
        <Card style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
          <div style={{ fontSize: 48 }}>✓</div>
          <h2 style={{ margin: 'var(--space-2) 0' }}>You're booked</h2>
          <p>A confirmation has been sent to {email}.</p>
        </Card>
      </div>
    );
  }

  const services = servicesQ.data.data;
  const selectedService = services.find((s) => s.id === serviceId);
  const slots = availQ.data?.data ?? [];

  return (
    <div style={{ maxWidth: 720, margin: '40px auto', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, margin: 0 }}>Book an appointment</h1>

      <Card>
        <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>Choose a service</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--space-2)' }}>
          {services.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setServiceId(s.id);
                setSlot(null);
              }}
              style={{
                padding: 'var(--space-3)',
                border: serviceId === s.id ? '2px solid var(--color-brand-primary)' : '1px solid var(--color-border-primary)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-primary)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 600 }}>{s.name}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {s.durationMinutes} min · {formatCents(s.priceCents)}
              </div>
            </button>
          ))}
        </div>
      </Card>

      {serviceId && (
        <Card>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>Pick a date and time</h3>
          <Field label="Date">
            <Input value={date} onChange={(v) => { setDate(v); setSlot(null); }} type="date" />
          </Field>
          <div style={{ marginTop: 'var(--space-3)' }}>
            {availQ.isLoading ? (
              <Spinner />
            ) : slots.length === 0 ? (
              <p style={{ color: 'var(--color-text-tertiary)' }}>No availability that day.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 'var(--space-2)' }}>
                {slots.map((s) => (
                  <button
                    key={s.startsAt}
                    onClick={() => setSlot(s)}
                    style={{
                      padding: 'var(--space-2)',
                      border: slot?.startsAt === s.startsAt ? '2px solid var(--color-brand-primary)' : '1px solid var(--color-border-primary)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-surface-primary)',
                      cursor: 'pointer',
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    {new Date(s.startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {slot && selectedService && (
        <Card>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>Your details</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!name) return setError('Name required');
              if (!email) return setError('Email required');
              bookMut.mutate();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
          >
            <Field label="Your name *">
              <Input value={name} onChange={setName} />
            </Field>
            <Field label="Email *">
              <Input value={email} onChange={setEmail} type="email" />
            </Field>
            <Field label="Phone">
              <Input value={phone} onChange={setPhone} type="tel" />
            </Field>
            <Field label="Notes">
              <Input value={notes} onChange={setNotes} />
            </Field>
            <div style={{ padding: 'var(--space-3)', background: 'var(--color-surface-secondary)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)' }}>
              <strong>{selectedService.name}</strong> · {new Date(slot.startsAt).toLocaleString()} · {formatCents(selectedService.priceCents)}
            </div>
            <ErrorBanner error={error} />
            <Button type="submit" disabled={bookMut.isPending}>
              {bookMut.isPending ? 'Booking…' : 'Confirm booking'}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}
